import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { EtapaProceso, ProcesoTrabajo } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Cargando } from "../../components/Cargando";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Icono } from "../../components/Icono";
import { Insignia } from "../../components/Insignia";
import { Tarjeta, TarjetaIndicador } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { GraficoBarras } from "../../components/GraficoBarras";
import { api, ErrorApi } from "../../lib/apiClient";
import { guardarEstadoVisual, leerEstadoVisual } from "../../lib/estadoVisualSesion";
import {
  calcularCuelloDeBotella,
  calcularDistribucionPorEtapa,
  calcularResumenProceso,
  type ColumnaKanban,
} from "../../lib/procesosVisual";
import { EtapaTarjetaVisual, type TogglesVisualizacion } from "./EtapaTarjetaVisual";

const TOGGLES_INICIALES: TogglesVisualizacion = {
  responsables: true,
  contadores: true,
  automatizaciones: true,
  tiempoObjetivo: true,
  checklist: true,
};

interface EstadoVisualGuardado {
  zoomPct: number;
  vistaCompacta: boolean;
  mostrarToggles: TogglesVisualizacion;
}

const ESTADO_VISUAL_POR_DEFECTO: EstadoVisualGuardado = {
  zoomPct: 100,
  vistaCompacta: false,
  mostrarToggles: TOGGLES_INICIALES,
};

function claveEstadoVisual(procesoId: string): string {
  return `disenador-visual:${procesoId}`;
}

// Diseñador Visual de Procesos: mapa de tarjetas conectadas por flechas en
// vez de la tabla clásica. Reutiliza 100% la arquitectura del Motor de
// Procesos de Trabajo — mismos endpoints (GET /etapas ya extendido con
// counts, GET /kanban ya extendido con dias_en_proceso, PATCH /etapas/orden
// nuevo pero dedicado) y comparte `etapas` con la Vista Tabla clásica (la
// pasa el padre) para no duplicar ese fetch; sólo el Kanban (cantidad y
// tiempo por etapa) se fetchea acá porque la tabla clásica no lo necesita.
export function DisenadorVisualProceso({
  proceso,
  etapas,
  puedeAdministrar,
  onCambio,
  onEditarProceso,
  onProcesoDuplicado,
  onEditarEtapa,
  onNuevaEtapa,
  onUsarPlantilla,
}: {
  proceso: ProcesoTrabajo;
  etapas: EtapaProceso[];
  puedeAdministrar: boolean;
  onCambio: () => void;
  onEditarProceso: () => void;
  onProcesoDuplicado: (proceso: ProcesoTrabajo) => void;
  onEditarEtapa: (etapa: EtapaProceso) => void;
  onNuevaEtapa: () => void;
  onUsarPlantilla: () => void;
}) {
  const { mostrar } = useToast();
  const location = useLocation();
  const rutaActual = location.pathname + location.search;
  const [columnas, setColumnas] = useState<ColumnaKanban[]>([]);
  const [cargandoKanban, setCargandoKanban] = useState(true);
  const [duplicandoProceso, setDuplicandoProceso] = useState(false);

  const [vistaCompacta, setVistaCompacta] = useState(false);
  const [zoomPct, setZoomPct] = useState(100);
  const [mostrarToggles, setMostrarToggles] = useState<TogglesVisualizacion>(TOGGLES_INICIALES);

  // Zoom/vista compacta/mostrar-ocultar son estado puramente visual y
  // temporal (no aportan nada al compartir un link) — se guardan en
  // sessionStorage por proceso para sobrevivir a un "volver" desde el
  // Kanban sin ensuciar la URL. Se recargan cada vez que cambia el
  // proceso abierto (no sólo al montar).
  useEffect(() => {
    const guardado = leerEstadoVisual(claveEstadoVisual(proceso.id), ESTADO_VISUAL_POR_DEFECTO);
    setZoomPct(guardado.zoomPct);
    setVistaCompacta(guardado.vistaCompacta);
    setMostrarToggles(guardado.mostrarToggles);
  }, [proceso.id]);

  useEffect(() => {
    guardarEstadoVisual(claveEstadoVisual(proceso.id), { zoomPct, vistaCompacta, mostrarToggles });
  }, [proceso.id, zoomPct, vistaCompacta, mostrarToggles]);

  const [ordenLocal, setOrdenLocal] = useState<EtapaProceso[] | null>(null);
  const [arrastrandoId, setArrastrandoId] = useState<string | null>(null);
  const [sobreId, setSobreId] = useState<string | null>(null);

  // Detección de saltos de fila del mapa: como las tarjetas usan flex-wrap
  // (varias por fila en desktop, una por fila en mobile), la flecha entre
  // la última tarjeta de una fila y la primera de la siguiente no debe
  // apuntar "hacia la derecha, al vacío" — se mide la posición real de cada
  // tarjeta (offsetTop, no afectado por el zoom porque transform:scale() es
  // sólo visual) y se cambia el glifo a "↓" en esos puntos. Sólo lectura de
  // layout, no toca el flex-wrap ni el drag&drop existentes.
  const refsFilaRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const [saltosFila, setSaltosFila] = useState<Set<number>>(new Set());

  async function cargarKanban() {
    setCargandoKanban(true);
    try {
      const datos = await api.get<{ proceso: ProcesoTrabajo; columnas: ColumnaKanban[] }>(`/procesos/${proceso.id}/kanban`);
      setColumnas(datos.columnas);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cargar el mapa del proceso.", "error");
    } finally {
      setCargandoKanban(false);
    }
  }

  useEffect(() => {
    cargarKanban();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proceso.id]);

  useEffect(() => {
    setOrdenLocal(null);
  }, [etapas]);

  const etapasOrdenadas = ordenLocal ?? [...etapas].sort((a, b) => a.orden - b.orden);
  const columnaPorEtapa = new Map(columnas.map((c) => [c.etapa.id, c]));

  const resumen = calcularResumenProceso(etapas, columnas);
  const distribucion = calcularDistribucionPorEtapa(columnas);
  const cuelloDeBotella = calcularCuelloDeBotella(columnas);

  const clavesEtapasOrdenadas = etapasOrdenadas.map((e) => e.id).join(",");

  useLayoutEffect(() => {
    function recalcularSaltosFila() {
      const tops = etapasOrdenadas.map((etapa) => refsFilaRef.current.get(etapa.id)?.offsetTop ?? 0);
      const saltos = new Set<number>();
      for (let i = 0; i < tops.length - 1; i++) {
        if (tops[i + 1] > tops[i] + 4) saltos.add(i);
      }
      setSaltosFila((prev) => {
        if (prev.size === saltos.size && [...prev].every((i) => saltos.has(i))) return prev;
        return saltos;
      });
    }
    recalcularSaltosFila();
    window.addEventListener("resize", recalcularSaltosFila);
    return () => window.removeEventListener("resize", recalcularSaltosFila);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clavesEtapasOrdenadas, vistaCompacta, mostrarToggles, cargandoKanban]);

  async function alDuplicarProceso() {
    setDuplicandoProceso(true);
    try {
      const datos = await api.post<{ proceso: ProcesoTrabajo }>(`/procesos/${proceso.id}/duplicar`, {});
      mostrar("Proceso duplicado.", "exito");
      onProcesoDuplicado(datos.proceso);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo duplicar el proceso.", "error");
    } finally {
      setDuplicandoProceso(false);
    }
  }

  async function alEliminarEtapa(etapa: EtapaProceso) {
    try {
      await api.patch(`/procesos/etapas/${etapa.id}/activo`, { activo: !etapa.activo });
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado de la etapa.", "error");
    }
  }

  async function alDuplicarEtapa(etapa: EtapaProceso) {
    try {
      const [checklist, documentos, automatizaciones] = await Promise.all([
        api.get<{ items: { texto: string; obligatorio: boolean }[] }>(`/procesos/etapas/${etapa.id}/checklist`),
        api.get<{ items: { nombre: string }[] }>(`/procesos/etapas/${etapa.id}/documentos-sugeridos`),
        api.get<{
          items: {
            tipo_actuacion: string;
            descripcion: string;
            genera_vencimiento: boolean;
            tipo_evento_vencimiento: string | null;
            dias_vencimiento: number | null;
            prioridad_vencimiento: string | null;
          }[];
        }>(`/procesos/etapas/${etapa.id}/automatizaciones`),
      ]);
      const creada = await api.post<{ etapa: EtapaProceso }>(`/procesos/${proceso.id}/etapas`, {
        nombre: `${etapa.nombre} (copia)`,
        color: etapa.color,
        orden: etapas.length,
        responsable_usuario_id: etapa.responsable_usuario_id,
        tiempo_objetivo_dias: etapa.tiempo_objetivo_dias,
        descripcion: etapa.descripcion,
      });
      const nuevaId = creada.etapa.id;
      await Promise.all([
        checklist.items.length > 0
          ? api.put(`/procesos/etapas/${nuevaId}/checklist`, { items: checklist.items })
          : Promise.resolve(),
        documentos.items.length > 0
          ? api.put(`/procesos/etapas/${nuevaId}/documentos-sugeridos`, { items: documentos.items })
          : Promise.resolve(),
        automatizaciones.items.length > 0
          ? api.put(`/procesos/etapas/${nuevaId}/automatizaciones`, { items: automatizaciones.items })
          : Promise.resolve(),
      ]);
      mostrar("Etapa duplicada.", "exito");
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo duplicar la etapa.", "error");
    }
  }

  async function alSoltarEtapa(destinoId: string) {
    setSobreId(null);
    const origenId = arrastrandoId;
    setArrastrandoId(null);
    if (!origenId || origenId === destinoId) return;
    const actuales = [...etapasOrdenadas];
    const origenIdx = actuales.findIndex((e) => e.id === origenId);
    const destinoIdx = actuales.findIndex((e) => e.id === destinoId);
    if (origenIdx < 0 || destinoIdx < 0) return;
    const [movida] = actuales.splice(origenIdx, 1);
    actuales.splice(destinoIdx, 0, movida);
    const reordenadas = actuales.map((e, i) => ({ ...e, orden: i }));
    setOrdenLocal(reordenadas);
    try {
      await api.patch(`/procesos/${proceso.id}/etapas/orden`, {
        ordenes: reordenadas.map((e) => ({ id: e.id, orden: e.orden })),
      });
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo reordenar las etapas.", "error");
      setOrdenLocal(null);
    }
  }

  return (
    <div className="space-y-6">
      <Tarjeta className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 shrink-0 rounded-full border border-borde" style={{ backgroundColor: proceso.color ?? undefined }} />
              <h3 className="text-base font-semibold text-texto">{proceso.nombre}</h3>
              <Insignia texto={proceso.activo ? "Activo" : "Inactivo"} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-texto-secundario">
              <span>{resumen.cantidadEtapas} etapas</span>
              <span>{resumen.cantidadExpedientesActivos} expedientes activos</span>
              <span>Tiempo promedio: {resumen.tiempoPromedioTotalDias != null ? `${resumen.tiempoPromedioTotalDias} días` : "Sin datos aún"}</span>
              <span>Tiempo objetivo total: {resumen.tiempoObjetivoTotalDias ?? "—"} días</span>
              {cuelloDeBotella && <span className="text-error">Cuello de botella: {cuelloDeBotella.etapaNombre}</span>}
            </div>
          </div>
          {puedeAdministrar && (
            <div className="flex flex-wrap gap-2">
              <Boton variante="secundario" tamano="sm" onClick={onEditarProceso}>
                Editar
              </Boton>
              <Boton variante="secundario" tamano="sm" onClick={alDuplicarProceso} cargando={duplicandoProceso}>
                Duplicar proceso
              </Boton>
              <Link to={`/procesos?proceso=${proceso.id}`} state={{ from: rutaActual }}>
                <Boton variante="secundario" tamano="sm">
                  Ver en Kanban
                </Boton>
              </Link>
              <Boton variante="secundario" tamano="sm" onClick={() => setVistaCompacta((v) => !v)}>
                {vistaCompacta ? "Vista Completa" : "Vista Compacta"}
              </Boton>
            </div>
          )}
        </div>
      </Tarjeta>

      {/* Barra de herramientas: Zoom + Mostrar + Leyenda en una sola fila
          horizontal (antes eran 2 paneles laterales de 200px/220px que le
          robaban ancho al mapa, dejando apenas ~1 tarjeta por fila — la
          causa real de que el mapa se viera como una lista vertical). Al
          liberar ese ancho, el mapa de abajo puede usar el ancho completo
          del contenido y las tarjetas (reutilizadas sin cambios) entran
          varias por fila. */}
      <Tarjeta className="p-4">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-texto-secundario">Zoom</span>
            <button
              type="button"
              onClick={() => setZoomPct((z) => Math.max(z - 10, 50))}
              className="rounded-lg border border-borde p-1.5 text-texto-secundario hover:text-texto"
              aria-label="Alejar"
            >
              <Icono nombre="zoom-menos" className="h-4 w-4" />
            </button>
            <span className="w-11 text-center text-sm text-texto">{zoomPct}%</span>
            <button
              type="button"
              onClick={() => setZoomPct((z) => Math.min(z + 10, 150))}
              className="rounded-lg border border-borde p-1.5 text-texto-secundario hover:text-texto"
              aria-label="Acercar"
            >
              <Icono nombre="zoom-mas" className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-texto">
            <span className="text-xs font-semibold uppercase tracking-wide text-texto-secundario">Mostrar</span>
            {(
              [
                ["responsables", "Responsables"],
                ["contadores", "Contadores"],
                ["automatizaciones", "Automatizaciones"],
                ["tiempoObjetivo", "Tiempo objetivo"],
                ["checklist", "Checklist"],
              ] as [keyof TogglesVisualizacion, string][]
            ).map(([clave, etiqueta]) => (
              <label key={clave} className="flex items-center gap-1.5 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={mostrarToggles[clave]}
                  onChange={(e) => setMostrarToggles((prev) => ({ ...prev, [clave]: e.target.checked }))}
                />
                {etiqueta}
              </label>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-texto-secundario">
            <span className="text-xs font-semibold uppercase tracking-wide text-texto-secundario">Leyenda</span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Insignia texto="En plazo" claveColor="Cumplido" /> en plazo
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Insignia texto="Vencida" claveColor="Vencido" /> vencida (cuello de botella)
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Icono nombre="arrastrar" className="h-4 w-4" /> arrastrar para reordenar
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Icono nombre="expandir-colapsar" className="h-4 w-4" /> checklist / documentos / automatizaciones
            </span>
            <span>"— / Nd": días promedio reales / días objetivo (— = sin datos)</span>
          </div>
        </div>
      </Tarjeta>

      {/* Mapa del proceso: ahora usa el ancho completo (ver comentario
          arriba). Se mantiene intacto el mismo flex-wrap + drag&drop nativo
          + EtapaTarjetaVisual ya existentes — sólo cambió qué lo rodea. */}
      <div className="overflow-x-auto">
        {cargandoKanban ? (
          <Cargando />
        ) : etapasOrdenadas.length === 0 ? (
          <EstadoVacio titulo="Este proceso todavía no tiene etapas" descripcion='Usá "+ Nueva etapa" para empezar a armar el mapa.' />
        ) : (
          <div
            className="flex flex-wrap items-start gap-3 pb-2"
            style={{ transform: `scale(${zoomPct / 100})`, transformOrigin: "top left" }}
          >
            {etapasOrdenadas.map((etapa, i) => {
              const columna = columnaPorEtapa.get(etapa.id);
              return (
                <div
                  key={etapa.id}
                  ref={(el) => {
                    if (el) refsFilaRef.current.set(etapa.id, el);
                    else refsFilaRef.current.delete(etapa.id);
                  }}
                  className="flex items-center gap-3"
                >
                  <EtapaTarjetaVisual
                    etapa={etapa}
                    indice={i}
                    cantidadExpedientes={columna?.expedientes.length}
                    tiempoPromedioDias={distribucion.find((d) => d.etapaId === etapa.id)?.tiempoPromedioDias ?? null}
                    vistaCompacta={vistaCompacta}
                    mostrar={mostrarToggles}
                    puedeAdministrar={puedeAdministrar}
                    draggable={puedeAdministrar}
                    arrastrando={arrastrandoId === etapa.id}
                    sobreDestino={sobreId === etapa.id}
                    onDragStart={() => setArrastrandoId(etapa.id)}
                    onDragOver={() => setSobreId(etapa.id)}
                    onDragLeave={() => setSobreId((cur) => (cur === etapa.id ? null : cur))}
                    onDrop={() => alSoltarEtapa(etapa.id)}
                    onDragEnd={() => {
                      setArrastrandoId(null);
                      setSobreId(null);
                    }}
                    onEditar={() => onEditarEtapa(etapa)}
                    onDuplicar={() => alDuplicarEtapa(etapa)}
                    onEliminar={() => alEliminarEtapa(etapa)}
                    onCambio={onCambio}
                  />
                  {i < etapasOrdenadas.length - 1 && (
                    <span className="shrink-0 text-lg text-texto-secundario" aria-hidden="true">
                      {saltosFila.has(i) ? "↓" : "→"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Panel inferior */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="grid grid-cols-2 gap-3">
          <TarjetaIndicador etiqueta="Etapas" valor={resumen.cantidadEtapas} />
          <TarjetaIndicador etiqueta="Expedientes activos" valor={resumen.cantidadExpedientesActivos} />
          <TarjetaIndicador etiqueta="Tiempo promedio" valor={resumen.tiempoPromedioTotalDias != null ? `${resumen.tiempoPromedioTotalDias}d` : "—"} />
          <TarjetaIndicador
            etiqueta="Tiempo objetivo"
            valor={resumen.tiempoObjetivoTotalDias != null ? `${resumen.tiempoObjetivoTotalDias}d` : "—"}
          />
        </div>
        <Tarjeta className="p-4">
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-texto-secundario">Distribución por etapa</h4>
          <GraficoBarras datos={distribucion.map((d) => ({ etiqueta: d.etapaNombre, valor: d.cantidad }))} />
        </Tarjeta>
        <Tarjeta className="p-4">
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-texto-secundario">Acciones rápidas</h4>
          <div className="flex flex-col gap-2">
            {puedeAdministrar && (
              <Boton variante="secundario" tamano="sm" onClick={onNuevaEtapa}>
                + Nueva etapa
              </Boton>
            )}
            {puedeAdministrar && (
              <Boton variante="secundario" tamano="sm" onClick={onUsarPlantilla}>
                Usar una plantilla
              </Boton>
            )}
            <Link to={`/procesos?proceso=${proceso.id}`} state={{ from: rutaActual }}>
              <Boton variante="secundario" tamano="sm" className="w-full">
                Ver en Kanban
              </Boton>
            </Link>
          </div>
        </Tarjeta>
      </div>
    </div>
  );
}
